import { ProgramClass } from '@/models/ProgramClass';
import { Reducer } from 'redux';
import { PURGE } from 'redux-persist';
import { ClassEnrollment, PersonWithEnrollment, ProgramClassState, ProgramClassTypes } from './types';

const INITIAL_STATE: ProgramClassState = {
    data: [],
    currentData: {} as ProgramClass,
    students: [],
    enrollments: [],
    enrollment: undefined,
    enrollmentsProcess: [],
    loading: false,
    success: false,
    hasError: false,
    updatingEnrollmentsCount: false,
};

const reducer: Reducer<ProgramClassState> = (state: ProgramClassState = INITIAL_STATE, action) => {
    let tmpStudents: PersonWithEnrollment[] = [];
    let tmpEnollments: ClassEnrollment[] = [];
    let tmpProgramClasses: ProgramClass[];
    switch (action.type) {
        case PURGE:
            return { ...INITIAL_STATE };
        case ProgramClassTypes.CREATE_PROGRAM_CLASS_FOR_COURSE_REQUEST:
            return { ...state, loading: true, success: false };
        case ProgramClassTypes.CREATE_PROGRAM_CLASS_FOR_PROGRAM_REQUEST:
            return { ...state, loading: true, success: false };
        case ProgramClassTypes.CREATE_PROGRAM_CLASS_SUCCESS:
            return { ...state, loading: false, success: true, data: [...state.data, action.payload] };
        case ProgramClassTypes.CREATE_PROGRAM_CLASS_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ProgramClassTypes.CREATE_PROGRAM_CLASS_CLEAR:
            return { ...state, currentData: {} as ProgramClass };
        case ProgramClassTypes.UPDATE_PROGRAM_CLASS_REQUEST:
            return { ...state, loading: true };
        case ProgramClassTypes.UPDATE_PROGRAM_CLASS_SUCCESS:
            tmpProgramClasses = [...state.data];
            const updatedClass: ProgramClass | undefined = tmpProgramClasses.find((x) => x.id === action.payload.id);
            if (updatedClass !== undefined) {
                tmpProgramClasses.splice(tmpProgramClasses.indexOf(updatedClass), 1, { ...updatedClass, ...action.payload });
            }
            return { ...state, loading: false, success: true, data: tmpProgramClasses };
        case ProgramClassTypes.UPDATE_PROGRAM_CLASS_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ProgramClassTypes.UPDATE_PROGRAM_CLASS_CLEAR:
            return { ...state, currentData: {} as ProgramClass };
        case ProgramClassTypes.DELETE_PROGRAM_CLASS_REQUEST:
            tmpProgramClasses = [...state.data];
            const classToDelete: ProgramClass | undefined = tmpProgramClasses.find((x) => x.id === action.payload);
            if (classToDelete !== undefined) {
                tmpProgramClasses.splice(tmpProgramClasses.indexOf(classToDelete), 1);
            }
            return { ...state, loading: true, data: tmpProgramClasses };
        case ProgramClassTypes.DELETE_PROGRAM_CLASS_SUCCESS:
            return { ...state, loading: false, success: true };
        case ProgramClassTypes.DELETE_PROGRAM_CLASS_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ProgramClassTypes.DELETE_PROGRAM_CLASS_CLEAR:
            return { ...state };
        case ProgramClassTypes.GET_PROGRAM_CLASS_REQUEST:
            return { ...state, loading: true };
        case ProgramClassTypes.GET_PROGRAM_CLASS_SUCCESS:
            return { ...state, loading: false, success: true, currentData: action.payload, updatingEnrollmentsCount: false };
        case ProgramClassTypes.GET_PROGRAM_CLASS_TO_UPDATE_ENROLLMENTS_SUCCESS:
            return {
                ...state,
                loading: false,
                success: true,
                updatingEnrollmentsCount: true,
                currentData: { ...state.currentData, enrollmentsCount: action.payload.enrollmentsCount },
            };
        case ProgramClassTypes.GET_PROGRAM_CLASS_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ProgramClassTypes.GET_PROGRAM_CLASS_CLEAR:
            return { ...state, currentData: {} as ProgramClass, currentProgramClassInEdition: false };
        case ProgramClassTypes.GET_ENROLLED_STUDENTS_REQUEST:
            return { ...state, loading: true };
        case ProgramClassTypes.GET_ENROLLED_STUDENTS_SUCCESS:
            return { ...state, loading: false, success: true, enrollments: action.payload };
        case ProgramClassTypes.GET_ENROLLED_STUDENTS_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ProgramClassTypes.GET_ENROLLED_STUDENTS_CLEAR:
            return { ...state, enrollments: [] };
        case ProgramClassTypes.GET_ENROLLMENT_BY_STUDENT_REQUEST:
            return { ...state, loading: true };
        case ProgramClassTypes.GET_ENROLLMENT_BY_STUDENT_SUCCESS:
            const classEnrollmentList: ClassEnrollment[] = action.payload;
            tmpStudents = [...state.students];
            tmpEnollments = [...state.enrollments];

            tmpStudents.forEach((student: PersonWithEnrollment) => {
                const ce: ClassEnrollment | undefined = classEnrollmentList.find((x) => x.personId === student.person.id);
                if (ce !== undefined) {
                    student.enrolled = true;
                    student.classEnrollmentId = ce.id;
                }
            });

            tmpEnollments.forEach((enrollment: ClassEnrollment) => {
                const ce: ClassEnrollment | undefined = classEnrollmentList.find((x) => x.personId === enrollment.personId);
                if (ce !== undefined) {
                    enrollment = ce;
                }
            });
            return { ...state, loading: false, success: true, students: tmpStudents, enrollments: tmpEnollments };
        case ProgramClassTypes.GET_ENROLLMENT_BY_STUDENT_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ProgramClassTypes.GET_ENROLLMENT_BY_STUDENT_CLEAR:
            return { ...state };
        case ProgramClassTypes.ENROLL_STUDENT_REQUEST:
            return { ...state, loading: true, enrollment: undefined };
        case ProgramClassTypes.ENROLL_STUDENT_SUCCESS:
            const ce: ClassEnrollment = action.payload;
            tmpStudents = [...state.students];
            tmpEnollments = [...state.enrollments];

            tmpStudents.forEach((student: PersonWithEnrollment) => {
                if (ce.personId === student.person.id) {
                    student.enrolled = true;
                    student.classEnrollmentId = ce.id;
                }
            });

            tmpEnollments.forEach((enrollment: ClassEnrollment) => {
                if (ce.personId === enrollment.personId) {
                    enrollment = ce;
                }
            });
            return { ...state, loading: false, success: true, students: tmpStudents, enrollments: tmpEnollments, enrollment: ce };
        case ProgramClassTypes.ENROLL_STUDENT_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ProgramClassTypes.ENROLL_STUDENT_CLEAR:
            return { ...state };
        case ProgramClassTypes.UNENROLL_STUDENT_REQUEST:
            return { ...state, loading: true };
        case ProgramClassTypes.UNENROLL_STUDENT_SUCCESS:
            tmpStudents = [...state.students];
            tmpEnollments = [...state.enrollments];

            const studentToUpdate: PersonWithEnrollment | undefined = tmpStudents.find((x) => x.classEnrollmentId === action.payload);
            if (studentToUpdate !== undefined) {
                studentToUpdate.enrolled = false;
                studentToUpdate.classEnrollmentId = Number(null);
            }

            const enrollToDelete: ClassEnrollment | undefined = tmpEnollments.find((x) => x.id === action.payload);
            if (enrollToDelete !== undefined) {
                tmpEnollments.splice(tmpEnollments.indexOf(enrollToDelete), 1);
            }

            return { ...state, loading: false, success: true, students: tmpStudents, enrollments: tmpEnollments };
        case ProgramClassTypes.UNENROLL_STUDENT_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ProgramClassTypes.UNENROLL_STUDENT_CLEAR:
            return { ...state };
        case ProgramClassTypes.GET_STUDENTS_WITH_ENROLL_INFO_REQUEST:
            return { ...state, loading: true };
        case ProgramClassTypes.GET_STUDENTS_WITH_ENROLL_INFO_SUCCESS:
            return { ...state, loading: false, success: true, students: action.payload };
        case ProgramClassTypes.GET_STUDENTS_WITH_ENROLL_INFO_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ProgramClassTypes.GET_STUDENTS_WITH_ENROLL_INFO_CLEAR:
            return { ...state, students: [] };
        case ProgramClassTypes.MASS_IMPORT_ENROLLMENT_REQUEST:
            return { ...state, loading: true, success: false, hasError: false };
        case ProgramClassTypes.MASS_IMPORT_ENROLLMENT_SUCCESS:
            return { ...state, loading: false, success: true, hasError: false };
        case ProgramClassTypes.MASS_IMPORT_ENROLLMENT_FAILURE:
            return { ...state, loading: false, success: false, hasError: true };
        default:
            return state;
    }
};

export default reducer;
